import { cn } from '@/utils/cn'
import { useMediaQuery } from '@/hooks/useMediaQuery'

/**
 * The Ledger mark: three rounded lines and a dot on the middle one.
 * Path from rktdno/claro-design tokens.json `icon.ledger` (100-unit grid,
 * viewBox 14 14 72 72), guideline v1.3: the middle line ends at 49 and the
 * dot sits at 68, a 6-unit gap. When it changes there, change it here.
 */
const LEDGER_LINES = [
  'M20 20H80A6 6 0 0 1 80 32H20A6 6 0 0 1 20 20Z',
  'M20 44H49A6 6 0 0 1 49 56H20A6 6 0 0 1 20 44Z',
  'M20 68H80A6 6 0 0 1 80 80H20A6 6 0 0 1 20 68Z',
] as const
const LEDGER_DOT = 'M61 50A7 7 0 1 0 75 50A7 7 0 1 0 61 50Z'

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)'

/**
 * How the mark moves (CLA-1817, the kickoff mockups; same motion as iOS
 * `LedgerMark.swift` and Android `ClaroLedger.kt`):
 * - `still`: the logo.
 * - `caret`: the dot blinks like a text caret. Claro has something for you
 *   (the "Claro suggestion" panels).
 * - `writing`: the dot blinks and the three lines write themselves in, left
 *   to right, hold, fade and start again. Claro is writing the note.
 *
 * The keyframes are `.ledger-caret` / `.ledger-line-*` in index.css, timed
 * from the claro-design motion tokens. Under prefers-reduced-motion the mark
 * is always still, dot on: checked here (so nothing animated is rendered)
 * and again in the stylesheet.
 */
export type LedgerMotion = 'still' | 'caret' | 'writing'

export function LedgerMark({
  size = 20,
  className,
  motion = 'still',
}: {
  size?: number
  className?: string
  motion?: LedgerMotion
}) {
  const reduce = useMediaQuery(REDUCED_MOTION, false)
  const m: LedgerMotion = reduce ? 'still' : motion
  return (
    <svg
      width={size}
      height={size}
      viewBox="14 14 72 72"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
      className={cn('shrink-0', className)}
      data-testid="ledger-mark"
      data-motion={m}
    >
      {LEDGER_LINES.map((d, i) => (
        <path
          key={d}
          d={d}
          data-ledger-part={`line-${i + 1}`}
          className={m === 'writing' ? `ledger-line ledger-line-${i + 1}` : undefined}
        />
      ))}
      <path d={LEDGER_DOT} data-ledger-part="dot" className={m === 'still' ? undefined : 'ledger-caret'} />
    </svg>
  )
}

interface ClaroLogoProps {
  /** `full` is the mark and the "Claro" wordmark; `mark` is the icon alone. */
  variant?: 'full' | 'mark'
  /** Height of the mark in px. The wordmark is sized to sit beside it. */
  size?: number
  className?: string
}

/**
 * Claro's logo, drawn in the current text colour (navy by default) so it can
 * sit on any surface: `className="text-surface"` reverses it on navy.
 * The wordmark is set in Onest 600, as in the brand guideline.
 */
export default function ClaroLogo({ variant = 'full', size = 20, className }: ClaroLogoProps) {
  if (variant === 'mark') {
    return (
      <span role="img" aria-label="Claro" className={cn('inline-flex text-claro-600', className)}>
        <LedgerMark size={size} />
      </span>
    )
  }
  return (
    <span className={cn('inline-flex items-center text-claro-600', className)} style={{ gap: size * 0.4 }}>
      <LedgerMark size={size} />
      <span
        className="font-semibold leading-none"
        style={{ fontSize: size * 0.95, letterSpacing: '-0.02em' }}
      >
        Claro
      </span>
    </span>
  )
}
